import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createLogger } from "logging";
import { ConfirmDeleteTarget } from "models/ConfirmDeleteTarget";
import { MeetingDetailModel } from "models/MeetingDetailModel";
import { ToggleType } from "models/ToggleType";
import { useApi, useCustomParams, usePermissions, useToasts, useTranslations } from "providers";
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";

import { ConfirmDeleteMeeting } from "./components/ConfirmDeleteMeeting";
import { MeetingContext } from "./MeetingContext";

const logger = createLogger("MeetingProvider");

const isExpandedDefault = false;
export const queryKey = "Meeting";

export const MeetingProvider: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const queryClient = useQueryClient();

  const [confirmDeleteMeeting, setConfirmDeleteMeeting] = useState<ConfirmDeleteTarget | undefined>(undefined);
  const [selectMeetingId, setSelectMeetingId] = useState<number>(-1);

  const [expanded, setExpanded] = useState<ToggleType>({});
  const [deleting, setDeleting] = useState<ToggleType>({});
  const [updating, setUpdating] = useState<ToggleType>({});

  const setMeetingExpanded = useCallback(
    (id: number, newState: boolean) => {
      logger.debug(`onToggle(${id}): `, newState);
      setExpanded({ ...expanded, [id]: newState });
    },
    [expanded]
  );

  const isMeetingExpanded = useCallback(
    (id: number, defaultValue?: boolean): boolean => {
      const isExpanded = expanded[id];
      if (isExpanded !== undefined) return isExpanded;
      if (id == selectMeetingId) return true;
      if (defaultValue !== undefined) return defaultValue;
      return isExpandedDefault;
    },
    [expanded, selectMeetingId]
  );

  const toggleMeetingExpanded = useCallback(
    (id: number) => {
      const before = expanded[id];
      const after = before == undefined ? !isExpandedDefault : !before;
      logger.debug(`toggleMeetingExpanded(${id}): `, before, after);
      setMeetingExpanded(id, after);
    },
    [setMeetingExpanded, expanded]
  );

  const isMeetingUpdating = useCallback(
    (id: number): boolean => {
      const flag = updating[id];
      if (flag !== undefined) return flag;
      return false;
    },
    [updating]
  );

  const setMeetingUpdating = useCallback((id: number, newState: boolean) => {
    setUpdating((prev) => ({ ...prev, [id]: newState }));
  }, []);

  const isMeetingDeleting = useCallback(
    (id: number): boolean => {
      const flag = deleting[id];
      if (flag !== undefined) return flag;
      return false;
    },
    [deleting]
  );

  const setMeetingDeleting = useCallback((id: number, newState: boolean) => {
    setDeleting((prev) => ({ ...prev, [id]: newState }));
  }, []);

  const { meetingId } = useCustomParams();
  const { handleHttpError } = usePermissions();
  const i18n = useTranslations();
  const toasts = useToasts();
  const { meetingApi } = useApi();

  // prettier-ignore
  const queryResult = useQuery({
    queryKey: [`Meeting|${meetingId}`],
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const meetingIdNumber = Number(meetingId)
      const data = await meetingApi.getDetail(meetingIdNumber, signal);
      logger.trace("Loaded", data);
      return data;
    },
    enabled: !!meetingId,
  });
  const { refetch, error } = queryResult;
  const meeting = queryResult.data;
  const isLoading = queryResult.isFetching || !meeting;

  useEffect(() => {
    handleHttpError(error);
  }, [error, handleHttpError]);

  const reloadMeeting = useCallback(() => {
    logger.debug(`reloadMeeting(): invalidating query`);
    queryClient.invalidateQueries({ queryKey: ["Meeting|"] });

    return refetch().then((result) => {
      if (!result.data) {
        return;
      }

      const meeting = result.data as MeetingDetailModel;
      toasts.refreshSuccess(i18n.Meeting, meeting?.title);
    });
  }, [refetch, toasts, i18n.Meeting, queryClient]);

  const contextValue = useMemo(
    () => ({
      value: meeting,
      meetingId,
      meeting: isLoading ? undefined : meeting,
      isLoading,
      reloadMeeting,
      confirmDeleteMeeting,
      setConfirmDeleteMeeting,
      selectMeetingId: selectMeetingId ? selectMeetingId : meeting?.id,
      setSelectMeetingId,
      isMeetingExpanded,
      toggleMeetingExpanded,
      setMeetingExpanded,
      isMeetingUpdating,
      setMeetingUpdating,
      isMeetingDeleting,
      setMeetingDeleting,
    }),
    [
      meeting,
      meetingId,
      isLoading,
      reloadMeeting,
      confirmDeleteMeeting,
      setConfirmDeleteMeeting,
      selectMeetingId,
      setSelectMeetingId,
      isMeetingExpanded,
      toggleMeetingExpanded,
      setMeetingExpanded,
      isMeetingUpdating,
      setMeetingUpdating,
      isMeetingDeleting,
      setMeetingDeleting,
    ]
  );

  return (
    <MeetingContext.Provider value={contextValue}>
      {children}
      <ConfirmDeleteMeeting />
    </MeetingContext.Provider>
  );
};
